import { pdfUnderlayBlobRefMatchesProject } from '@shared/helpers/blob-ref.helpers';
import type { BuildingDesign } from '@shared/models/design/building-design.model';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { Project } from '@shared/models/project.model';

/** Sentinel used while blueprint rasters are stripped for worker transfer. */
export const BLUEPRINT_IMAGE_TRANSFER_PLACEHOLDER = '';

type BlueprintImageById = Record<string, string>;

/**
 * Drop client-only {@link BlueprintUnderlay.imageDataUrl} so it never hits JSONB.
 */
export const omitBlueprintRasterFromUnderlay = (
  underlay: BlueprintUnderlay,
): BlueprintUnderlay => {
  if (underlay.imageDataUrl === undefined) return underlay;
  const { imageDataUrl: _raster, ...rest } = underlay;
  return rest;
};

/**
 * Map each underlay on {@link design}. Same design reference when nothing changes.
 */
const mapDesignUnderlays = (
  design: BuildingDesign,
  mapUnderlay: (underlay: BlueprintUnderlay) => BlueprintUnderlay,
): BuildingDesign => {
  if (!design.blueprintUnderlays?.length) return design;
  const blueprintUnderlays = design.blueprintUnderlays.map(mapUnderlay);
  const changed = blueprintUnderlays.some(
    (underlay, index) => underlay !== design.blueprintUnderlays?.[index],
  );
  return changed ? { ...design, blueprintUnderlays } : design;
};

/**
 * Replace {@link Project.design} when the mapper returns a new object.
 */
const withMappedProjectDesign = (
  project: Project,
  mapDesign: (design: BuildingDesign) => BuildingDesign,
): Project => {
  const design = mapDesign(project.design);
  return design === project.design ? project : { ...project, design };
};

/**
 * Strip rasters from every underlay on a project (persist / socket).
 */
export const omitBlueprintRastersFromProject = (project: Project): Project =>
  withMappedProjectDesign(project, (design) =>
    mapDesignUnderlays(design, omitBlueprintRasterFromUnderlay),
  );

/**
 * Collect client-only raster data-URLs keyed by underlay id.
 * Does not mutate {@link project}.
 */
export const collectBlueprintImageDataUrls = (
  project: Project,
): BlueprintImageById => {
  const imagesById: BlueprintImageById = {};
  for (const underlay of project.design.blueprintUnderlays ?? []) {
    if (
      underlay.imageDataUrl &&
      underlay.imageDataUrl !== BLUEPRINT_IMAGE_TRANSFER_PLACEHOLDER
    ) {
      imagesById[underlay.id] = underlay.imageDataUrl;
    }
  }
  return imagesById;
};

type StripBlueprintImagesResult = {
  project: Project;
  imagesById: BlueprintImageById;
};

/**
 * Collect raster data-URLs from a design underlay list.
 */
const collectImagesFromDesign = (
  design: BuildingDesign,
  imagesById: BlueprintImageById,
): BuildingDesign => {
  if (!design.blueprintUnderlays?.length) return design;

  const blueprintUnderlays = design.blueprintUnderlays.map((underlay) => {
    if (underlay.imageDataUrl) {
      imagesById[underlay.id] = underlay.imageDataUrl;
      return {
        ...underlay,
        imageDataUrl: BLUEPRINT_IMAGE_TRANSFER_PLACEHOLDER,
      };
    }
    return underlay;
  });

  return { ...design, blueprintUnderlays };
};

/**
 * Restore raster data-URLs onto a design underlay list.
 */
const restoreImagesOnDesign = (
  design: BuildingDesign,
  imagesById: BlueprintImageById,
): BuildingDesign => {
  if (!design.blueprintUnderlays?.length) return design;

  let changed = false;
  const blueprintUnderlays = design.blueprintUnderlays.map((underlay) => {
    const imageDataUrl = imagesById[underlay.id];
    if (!imageDataUrl || underlay.imageDataUrl === imageDataUrl)
      return underlay;
    changed = true;
    return { ...underlay, imageDataUrl };
  });

  return changed ? { ...design, blueprintUnderlays } : design;
};

/**
 * Strip large blueprint `imageDataUrl` values before posting a project to the
 * enrich worker. Rasters are unused by enrichment and can crash structured clone.
 */
export const stripBlueprintImagesForWorkerTransfer = (
  project: Project,
): StripBlueprintImagesResult => {
  const imagesById: BlueprintImageById = {};
  const design = collectImagesFromDesign(project.design, imagesById);
  return {
    project: design === project.design ? project : { ...project, design },
    imagesById,
  };
};

/**
 * Re-apply stripped blueprint rasters after the enrich worker returns.
 */
export const restoreBlueprintImagesAfterWorkerTransfer = (
  project: Project | null,
  imagesById: BlueprintImageById,
): Project | null => {
  if (!project) return project;
  if (!Object.keys(imagesById).length) return project;
  return withMappedProjectDesign(project, (design) =>
    restoreImagesOnDesign(design, imagesById),
  );
};

type EnrichOptionsWithPrev = { prevProject?: Project };

/**
 * Strip rasters from enrich options prevProject the same way as the payload.
 */
export const stripBlueprintImagesFromEnrichOptions = <
  T extends EnrichOptionsWithPrev,
>(
  options: T,
  imagesById: BlueprintImageById,
): T => {
  if (!options.prevProject) return options;
  const stripped = stripBlueprintImagesForWorkerTransfer(options.prevProject);
  Object.assign(imagesById, stripped.imagesById);
  if (stripped.project === options.prevProject) return options;
  return { ...options, prevProject: stripped.project };
};

type StoredBlueprintRasterFields = {
  imageDataUrl?: string;
  imageRef?: string;
};

/**
 * Collect stored raster fields from a project, keyed by underlay id.
 */
const collectStoredBlueprintRasterFields = (
  project: Project,
): Record<string, StoredBlueprintRasterFields> => {
  const byId: Record<string, StoredBlueprintRasterFields> = {};
  for (const underlay of project.design.blueprintUnderlays ?? []) {
    byId[underlay.id] = {
      ...(underlay.imageDataUrl ? { imageDataUrl: underlay.imageDataUrl } : {}),
      ...(underlay.imageRef ? { imageRef: underlay.imageRef } : {}),
    };
  }
  return byId;
};

/**
 * Fill blank raster fields on {@link incoming} from {@link stored}, matched by
 * underlay id.
 *
 * Socket payloads omit `imageDataUrl`. A second client (or the metadata-first
 * save before PUT) may also omit {@link BlueprintUnderlay.imageRef}. Without
 * this merge the next persist treats the row as `kind: 'none'` and drops the
 * object key. Non-empty incoming values always win.
 */
export const fillMissingBlueprintImagesFromStored = (
  stored: Project,
  incoming: Project,
): Project => {
  const storedById = collectStoredBlueprintRasterFields(stored);
  if (!Object.keys(storedById).length) return incoming;

  return withMappedProjectDesign(incoming, (design) => {
    if (!design.blueprintUnderlays?.length) return design;

    let designChanged = false;
    const blueprintUnderlays = design.blueprintUnderlays.map((underlay) => {
      const fromStored = storedById[underlay.id];
      if (!fromStored) return underlay;

      let next = underlay;
      if (!next.imageRef && fromStored.imageRef) {
        next = { ...next, imageRef: fromStored.imageRef };
      }
      if (next === underlay) return underlay;
      designChanged = true;
      return next;
    });

    return designChanged ? { ...design, blueprintUnderlays } : design;
  });
};

/**
 * Drop {@link BlueprintUnderlay.imageRef} values that are not this project's
 * canonical blob key so GET cannot 403 on a Duplicate row after a failed copy.
 */
export const clearNonCanonicalPdfUnderlayImageRefs = (
  organization: string,
  project: Project,
): Project =>
  withMappedProjectDesign(project, (design) => {
    if (!design.blueprintUnderlays?.length) return design;

    let designChanged = false;
    const blueprintUnderlays = design.blueprintUnderlays.map((underlay) => {
      if (!underlay.imageRef) return underlay;
      if (
        pdfUnderlayBlobRefMatchesProject(
          underlay.imageRef,
          organization,
          project.id,
          underlay.id,
        )
      ) {
        return underlay;
      }
      const { imageRef: _dropped, ...rest } = underlay;
      designChanged = true;
      return rest;
    });

    return designChanged ? { ...design, blueprintUnderlays } : design;
  });

/**
 * Socket snapshots omit rasters. Copy stored {@link BlueprintUnderlay.imageRef}
 * onto incoming rows; never copy {@link BlueprintUnderlay.imageDataUrl} onto the
 * project document (rasters live in the client cache / blob store).
 */
export const preserveBlueprintImagesFromPrevious = (
  previous: Project,
  incoming: Project,
): Project =>
  fillMissingBlueprintImagesFromStored(
    previous,
    omitBlueprintRastersFromProject(incoming),
  );
